import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { PartnerOnboardingService } from "../../../api/generated/services/PartnerOnboardingService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface RetryOnboardingModalProps {
  isOpen: boolean;
  applicationId: string;
  companyName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RetryOnboardingModal: React.FC<RetryOnboardingModalProps> = ({
  isOpen,
  applicationId,
  companyName,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await PartnerOnboardingService.postApiPartnerOnboardingRetryPartnerApplicationFromLastError({
        partnerApplicationId: applicationId,
      });
      setError(null);
      setLoading(false);
      onClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error retrying onboarding:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to retry onboarding. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal-content"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Retry Onboarding"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Retry Onboarding</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Are you sure you want to retry the failed onboarding steps for{" "}
          <span className="font-medium">{companyName}</span>?
        </p>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={loading}>
            Retry
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RetryOnboardingModal;
